import React, { useState, useEffect } from 'react';
import { Box, Button, TextField, Paper, Dialog, DialogTitle, DialogContent, DialogActions, Alert } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { apiGet, apiRequest } from '../../../utils/fetch';
import { EmailEventInterface, GenericResponseInterface } from '../../../interfaces/interfaces';
import { getEmailEventsColumns } from './EmailEventsColumns';
import { EmailEventsUpdateDialog } from './EmailEventsUpdateDialog';

const EmailEventsAdminList: React.FC = () => {
    const [events, setEvents] = useState<EmailEventInterface[]>([]);
    const [singleEvent, setSingleEvent] = useState<EmailEventInterface | null>(null);
    const [eventId, setEventId] = useState('');
    const [eventName, setEventName] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [eventToDelete, setEventToDelete] = useState<EmailEventInterface | null>(null);
    const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
    const [eventToUpdate, setEventToUpdate] = useState<EmailEventInterface | null>(null);
    const [updateError, setUpdateError] = useState<string | null>(null);

    // Fetch all events
    const handleGetAllEvents = async () => {
        setError(null);
        setSingleEvent(null);
        setSuccess(null);
        try {
            const result = await apiGet<GenericResponseInterface<EmailEventInterface[]>>('/emailEvents');
            if (result.status && result.data) {
                setEvents(result.data);
            } else {
                setError(result.message || 'Failed to fetch events');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to fetch events');
        }
    };

    // Fetch event by id
    const handleGetEventById = async () => {
        setError(null);
        setEvents([]);
        setSuccess(null);
        if (!eventId) return setError('Please enter an Event ID');
        try {
            const result = await apiGet<GenericResponseInterface<EmailEventInterface>>(`/admin/emailEvents/${eventId}`);
            if (result.status && result.data) {
                setSingleEvent(result.data);
            } else {
                setError(result.message || 'Failed to fetch event');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to fetch event');
        }
    };

    // Fetch event by name
    const handleGetEventByName = async () => {
        setError(null);
        setEvents([]);
        setSuccess(null);
        if (!eventName) return setError('Please enter an Event Name');
        try {
            const result = await apiGet<GenericResponseInterface<EmailEventInterface>>(`/admin/emailEvents/name/${eventName}`);
            if (result.status && result.data) {
                setSingleEvent(result.data);
            } else {
                setError(result.message || 'Failed to fetch event');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to fetch event');
        }
    };

    // Refresh cache
    const handleRefreshCache = async () => {
        setError(null);
        setSuccess(null);
        try {
            const result = await apiRequest<GenericResponseInterface>('/admin/emailEvents/refresh-cache', 'POST');
            if (result.status) {
                setSuccess(result.message || 'Email events cache refreshed successfully!');
            } else {
                setError(result.message || 'Failed to refresh cache');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to refresh cache');
        }
    };

    // Delete event
    const handleDeleteEvent = async () => {
        if (!eventToDelete) return;
        setError(null);
        setSuccess(null);
        try {
            const result = await apiRequest<GenericResponseInterface>(`/admin/emailEvents/delete/${eventToDelete.id}`, 'DELETE');
            if (result.status) {
                setSuccess(result.message || 'Event deleted successfully. Cache auto-refreshed.');
                setDeleteDialogOpen(false);
                setEventToDelete(null);
                handleGetAllEvents();
            } else {
                setError(result.message || 'Failed to delete event');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to delete event');
        }
    };

    // Update event
    const handleUpdateEvent = async () => {
        if (!eventToUpdate || !eventToUpdate.id) return;
        setUpdateError(null);
        setSuccess(null);

        try {
            const result = await apiRequest<GenericResponseInterface<EmailEventInterface>>(
                `/admin/emailEvents/update/${eventToUpdate.id}`,
                'PUT',
                eventToUpdate
            );
            if (result.status) {
                setSuccess(result.message || 'Event updated successfully. Cache auto-refreshed.');
                setUpdateDialogOpen(false);
                setEventToUpdate(null);
                handleGetAllEvents();
            } else {
                setUpdateError(result.message || 'Failed to update event');
            }
        } catch (err: any) {
            setUpdateError(err.message || 'Failed to update event');
        }
    };

    const columns = getEmailEventsColumns(
        setEventToDelete,
        setDeleteDialogOpen,
        setEventToUpdate,
        setUpdateDialogOpen
    );

    useEffect(() => {
        handleGetAllEvents();
    }, []);

    return (
        <Box>
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <Button variant="contained" onClick={handleGetAllEvents}>Get All Events</Button>
                <Button variant="contained" color="secondary" onClick={handleRefreshCache}>
                    🔄 Refresh Cache
                </Button>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                        label="Event ID"
                        value={eventId}
                        onChange={e => setEventId(e.target.value)}
                        size="small"
                    />
                    <Button variant="contained" onClick={handleGetEventById}>Get by ID</Button>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                        label="Event Name"
                        value={eventName}
                        onChange={e => setEventName(e.target.value)}
                        size="small"
                    />
                    <Button variant="contained" onClick={handleGetEventByName}>Get by Name</Button>
                </Box>
            </Box>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
            <Paper sx={{ width: '100%', overflow: 'auto', mt: 2 }}>
                {events.length > 0 && (
                    <DataGrid
                        rows={events.map(e => ({ ...e, id: e.id ?? Math.random() }))}
                        columns={columns}
                        initialState={{
                            pagination: { paginationModel: { pageSize: 10, page: 0 } }
                        }}
                        pageSizeOptions={[10, 25, 50]}
                        autoHeight
                    />
                )}
                {singleEvent && (
                    <DataGrid
                        rows={[{ ...singleEvent, id: singleEvent.id ?? Math.random() }]}
                        columns={columns}
                        hideFooter
                        autoHeight
                    />
                )}
            </Paper>
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    Are you sure you want to delete this email event? Cache will be auto-refreshed.
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button color="error" onClick={handleDeleteEvent}>Delete</Button>
                </DialogActions>
            </Dialog>
            
            <EmailEventsUpdateDialog
                open={updateDialogOpen}
                eventToUpdate={eventToUpdate}
                setEventToUpdate={setEventToUpdate}
                onClose={() => {
                    setUpdateDialogOpen(false);
                    setSuccess(null);
                }}
                onSave={handleUpdateEvent}
                updateError={updateError}
                updateSuccess={success}
            />
        </Box>
    );
};

export default EmailEventsAdminList;
